// The Agent Tasks list's two filter axes — **Space, then Agent** — and the pure logic behind them.
//
// Modelled on `file-browser.ts`, which is the shipped precedent for a filter row in this product
// (`FilesFilterBar` on web, `FilesFilterRow` on both phones). Three of its conventions are carried over
// deliberately, because each one prevents a defect this screen would otherwise have:
//
//   1. **Options are DERIVED from the tasks in scope**, never from the full Space/agent roster. An account
//      can hold nine agents of which three have ever run a task; offering the other six is offering six ways
//      to empty the screen. A menu must not contain a value that matches nothing.
//   2. **A control with nothing to choose between is not rendered at all.** On a single-Space account the
//      Space picker is noise, and on both accounts this work was designed against, the Agent picker would
//      have been.
//   3. **The label states the current value**, so the row reads as an answer rather than a prompt.
//
// The state is ids, not names: names are display, and two Spaces can be renamed to the same thing.

export interface FilterableTask {
  workspaceId?: string;
  agentId?: string;
}

export interface FilterOption {
  id: string;
  name: string;
}

/** Which Space and which agent the list is narrowed to. `''` on either axis means "all". */
export interface TaskFilterState {
  workspaceId: string;
  agentId: string;
}

export const NO_TASK_FILTERS: TaskFilterState = { workspaceId: '', agentId: '' };

/** Whether anything is narrowed — drives the Clear affordance. */
export function hasActiveTaskFilters(filters: TaskFilterState): boolean {
  return activeTaskFilterCount(filters) > 0;
}

/** How many axes are narrowed — the N in "Clear filters · N". */
export function activeTaskFilterCount(filters: TaskFilterState): number {
  return (filters.workspaceId ? 1 : 0) + (filters.agentId ? 1 : 0);
}

/**
 * The Spaces worth offering: those that actually own a task here, in the roster's own order so the picker
 * reads like the sidebar rather than a shifting most-recent list. A Space the roster hasn't resolved yet is
 * skipped rather than shown by id.
 */
export function spacesOf(tasks: FilterableTask[], workspaces: FilterOption[]): FilterOption[] {
  const present = new Set(tasks.map((task) => task.workspaceId).filter(Boolean));
  return workspaces.filter((workspace) => present.has(workspace.id));
}

/** The agents worth offering, on the same rule: only those with a task in scope, in roster order. */
export function agentsOf(tasks: FilterableTask[], agents: FilterOption[]): FilterOption[] {
  const present = new Set(tasks.map((task) => task.agentId).filter(Boolean));
  return agents.filter((agent) => present.has(agent.id));
}

/**
 * Whether a picker earns its place. One option is not a choice — it names the only thing there is, which
 * the list already says — so a control appears only once there are two things to pick between.
 */
export function offersAChoice(options: FilterOption[]): boolean {
  return options.length > 1;
}

export function filterTasks<T extends FilterableTask>(tasks: T[], filters: TaskFilterState): T[] {
  return tasks.filter(
    (task) =>
      (!filters.workspaceId || task.workspaceId === filters.workspaceId) &&
      (!filters.agentId || task.agentId === filters.agentId),
  );
}

/**
 * Drop a selection that the tasks in scope no longer support, so a filter can never strand the list on an
 * empty screen with no visible cause. Happens for real: narrow to an agent, then narrow to a Space that
 * agent has never worked in, and without this the screen goes blank while both pickers still read as set.
 */
export function clampTaskFilters(
  filters: TaskFilterState,
  spaces: FilterOption[],
  agents: FilterOption[],
): TaskFilterState {
  return {
    workspaceId: spaces.some((space) => space.id === filters.workspaceId) ? filters.workspaceId : '',
    agentId: agents.some((agent) => agent.id === filters.agentId) ? filters.agentId : '',
  };
}
